import moment from "moment";
import * as React from "react";
import { Column, RowRenderProps } from "react-table";

/**
 * Column descriptions
 *
 * We ask the server to send us which columns exist and later we map this to
 * ReactTable columns
 */
export interface ColumnDescription {
  /** Subcolumns for groups */
  children?: Record<string, ColumnDescription>;

  /** Title to use for the column */
  label: string;

  /**
   * Type of content, used to decide which cell renderer to use and
   * how to handle the filter form is isFilterable is true
   *
   * @TODO: Make enum
   */
  type:
    | "hidden"
    | "text"
    | "dropdown"
    | "header"
    | "date"
    | "date-present"
    | "month"
    | "bool-dropdown"
    | "checkbox"
    | "mail"
    | "url"
    | "enum";

  /** Min width of column */
  width: number;

  /** Is this column visible on the table? */
  visible: boolean;

  /** Does this column appear on the filter form */
  filterable: boolean;

  /** For bool-dropdown types, what are the options */
  options?: string[];
}

/**
 * Generate a function that renders a column
 *
 * This is wrapped in a function because the render() function does not pass the
 * column object so we can't know `col.options` for the field types that need it.
 * This way, we generate a function for each column, maybe this isn't performant
 * though (one lambda for each column, even if they are the same).
 *
 * @see {IColumn.type}
 */
export function generateColumnRenderer(col: ColumnDescription) {
  switch (col.type) {
    case "dropdown":
      return undefined;
    case "url":
      return (row: RowRenderProps) => {
        if (row.value) {
          return (
            <a href={row.value} target="_blank" rel="noopener noreferrer">
              Link
            </a>
          );
        } else {
          return <span>N/A</span>;
        }
      };
    case "mail":
      return (row: RowRenderProps) => {
        if (row.value) {
          const mailURL = `mail:${row.value}`;
          return (
            <a href={mailURL} target="_blank" rel="noopener noreferrer">
              {row.value}
            </a>
          );
        } else {
          return <span>N/A</span>;
        }
      };
    case "checkbox":
      return (row: RowRenderProps) =>
        row.value ? <div>Yes</div> : <div>No</div>;
    case "bool-dropdown":
      return (row: RowRenderProps) => {
        if (col.options === undefined) {
          return row.value ? <div>Yes</div> : <div>No</div>;
        } else {
          return row.value ? (
            <div>{col.options[0]}</div>
          ) : (
            <div>{col.options[1]}</div>
          );
        }
      };
    case "month":
      return (row: RowRenderProps) => {
        if (row.value === null || row.value === undefined) {
          return <div>Present</div>;
        }

        const m = moment(row.value, "YYYY-MM-DD");
        return <div>{m.format("MMM YYYY")}</div>;
      };
    case "date":
      return (row: RowRenderProps) =>
        row.value ? <div>{row.value}</div> : <div>N/A</div>;
    case "date-present":
      return (row: RowRenderProps) =>
        row.value ? <div>{row.value}</div> : <div>Present</div>;
    case "enum":
      return (row: RowRenderProps) => {
        if (col.options === undefined || col.options.length === 0) {
          console.error(
            '[Columns -> generateColumnRenderer()] Column type is "enum" but `column.options` is undefined or empty'
          );
          return <div>{row.value}</div>;
        } else {
          return <div>{col.options[row.value]}</div>;
        }
      };
    default:
      console.warn(`Unknown column type ${col.type}!`);
      return undefined;
  }
}

/** Converts a column description from the server to a ReactTable column */
export function columnToReactTableColumn(
  key: string,
  col: ColumnDescription
): Column {
  // Recursively convert children
  let children = undefined;
  if (col.children !== undefined) {
    children = [];
    for (const childKey of Object.keys(col.children)) {
      children.push(columnToReactTableColumn(childKey, col.children[childKey]));
    }
  }

  return {
    columns: children,
    Header: col.label,
    Cell: generateColumnRenderer(col),
    accessor: key,
    show: col.visible === undefined ? true : col.visible,
    id: key,
  };
}
